import { map, filter, pluck } from 'ramda'
import React from 'react'
import ReactDOM from 'react-dom'
import styled, { ThemeProvider } from 'styled-components'

import FullscreenOnlyLayout from './layouts/fullscreenOnlyLayout.js'
import MapOnlyLayout from './layouts/mapOnlyLayout.js'
import ModEditorLayout from './layouts/modEditorLayout.js'
import ModLayout from './layouts/modLayout.js'
import SearchDirectoryLayout from './layouts/searchDirectoryLayout.js'
import StandardLayout from './layouts/standardLayout.js'

const layouts = {
  standard: StandardLayout,
  searchDirectory: SearchDirectoryLayout,
  fullscreen: FullscreenOnlyLayout,
  mapOnly: MapOnlyLayout,
  modEditor: ModEditorLayout,
  mod: ModLayout
}

// sort by the order property.
const orderComparator = (w1, w2) => {
  if (w1.order == null) // null or undefined
    return w2.order == null ? 0 : 1 // any order is higher pri than none
  if (w2.order == null)
    return -1
  return w1.order - w2.order
}

const AppMain = styled.div`
  height: 100%;
`
class LayerManager extends React.Component {
  constructor (props) {
    super(props)
    this.state = {
      layers: {},
      currentLayout: props.initialLayout || 'standard',
      mapLoaded: false,
      previousLayer: ''
    }
  }

  // Before updating layers, check for any shouldShow triggers...
  updateLayers (layers) {
    let dirty = true // continue until we run through with no updates - init to true so we do at least one loop
    while (dirty) {
      const currentlyShowing = this.getShowingLayersArray()
      dirty = false // set to false for next run - set to true when a layer show state changes
      Object.values(this.state.layers)
        .filter(layer => layer.shouldShow)
        .forEach(layer => {
          const shouldShowValue = layer.shouldShow(currentlyShowing)
          if (shouldShowValue !== layer.show) { // a change
            layer.show = !!shouldShowValue
            dirty = true
          }
        })
    }
    this.setState({ ...this.state, layers })

    const bus = this.props.app.bus
    bus.send('screen/displayHeader', { display: true })
  }

  componentDidMount () {
    const bus = this.props.app.bus
    bus.on('layers/register', args => this.updateLayers({ ...this.state.layers, [args.id]: { show: false, isOverlay: false, ...args } }))
    bus.on('layers/show', ({ id }) => {
      const target = this.state.layers[id]
      if (!target) {
        this.props.log.warn(`Trying to show an unknown layer - ${id}`)
        return
      }
      const newLayers = map((layer) => {
        if (layer.id === id) {
          return { ...layer, show: true }
        } else if (layer.layoutId === target.layoutId && !layer.isOverlay && !target.isOverlay) {
          return { ...layer, show: false }
        } else {
          return layer
        }
      }, this.state.layers)
      const currentLayout = target.layoutName || this.state.currentLayout
      this.updateLayers(newLayers)
      this.setState({ ...this.state, currentLayout })
    })
    bus.on('layers/hide', ({ id }) => this.updateLayers({ ...this.state.layers, [id]: { ...this.state.layers[id], show: false } }))
    bus.on('layers/hideWithLayoutId', (layoutId) => {
      bus.send('layers/hideMultiple', Object.keys(pluck('id')(filter((layer) => layer.layoutId === layoutId, this.state.layers))))
    })
    bus.on('layers/hideMultiple', (ids) => {
      const newLayers = { ...this.state.layers }
      ids.forEach(id => {
        if (newLayers[id]) {
          newLayers[id].show = false
        }
      })
      this.updateLayers(newLayers)
    })
    bus.on('layers/changeLayout', ({ layoutName }) => {
      this.setState({ ...this.state, currentLayout: layoutName })
    })

    bus.on('map/mapReadyToShow', () => this.setState({ ...this.state, mapLoaded: true }))

    bus.on('i18n/langChanged', () => this.rerender())

    bus.on('layers/isShowing', async ({ id }) => {
      const visibleLayers = filter((layer) => {
        return layer.id === id && layer.show === true
      }, this.state.layers)
      return Object.keys(visibleLayers).length > 0
    })

    bus.on('layers/getLayers', () => this.state.layers)
    bus.on('layers/getVisibleLayersIds', () => this.getShowingLayersArray())
    bus.on('layers/getVisibleLayersByLayoutId', (id) => this.getVisibleLayerByLayoutId(id))
    bus.on('layers/getPreviousLayer', () => this.state.previousLayer)
    bus.on('layers/setPreviousLayer', (id) => this.setState({ previousLayer: id }))
    bus.on('layers/rerender', () => this.rerender())
    bus.on('env/resize', () => this.rerender())
  }

  // This is to force a rerender of the layer manager
  rerender () {
    this.updateLayers(this.state.layers)
  }

  getShowingLayersArray () {
    const visibleLayers = filter((layer) => {
      return layer.show === true
    }, this.state.layers)
    return Object.keys(visibleLayers)
  }

  getVisibleLayerByLayoutId (layoutId) {
    const visibleLayers = filter((layer) => {
      return layer.show === true && layer.layoutId === layoutId
    }, this.state.layers)
    return Object.keys(visibleLayers)
  }

  possiblyShowLogin (content) {
    const $login = this.state.layers.login
    if ($login.show)
      content = <$login.widget key={`widget-login`} />
    return content
  }

  render () {
    const layers = Object.values(this.state.layers)
      .sort(orderComparator)
      .map($widget =>
        $widget.show
          ? <$widget.widget key={`widget-${$widget.id}`} layoutId={$widget.layoutId} widgetType={$widget.widgetType} />
          : null)
    const Layout = layouts[this.state.currentLayout]
    const Loading = this.props.Loading
    const content = <Layout
      app={this.props.app}
      uiHide={ this.props.app.config.uiHide || { } }
      Loading={Loading}
      mapLoaded={this.state.mapLoaded}
    >
      {layers}
    </Layout>

    if (this.state.layers.login)
      return this.possiblyShowLogin(content)

    return content
  }
}

const initLayerManager = async app => {
  const config = app.config
  const log = app.log.sublog('layerManager')
  const { getTheme, GlobalStyle, Loading, getThemeState } = app.themePack
  const renderDiv = config.renderDiv || '#renderDiv'
  const renderElement = document.querySelector(renderDiv)
  if (!renderElement)
    throw Error(`DOM element not found matching selector: ${renderDiv}`)

  const renderAppRoot = () => {
    ReactDOM.render(
      <ThemeProvider theme={ getTheme() }>
        <AppMain className="atrius-map">
          <GlobalStyle />
          <LayerManager app={app} initialLayout={config.initialLayout} Loading={Loading} log={log} />
        </AppMain>
      </ThemeProvider>, renderElement)
  }

  renderAppRoot()

  app.bus.on('app/unmount', () => ReactDOM.unmountComponentAtNode(renderElement))

  getThemeState().addCallback(renderAppRoot)
}

const destroy = async app => {
  const renderDiv = app.config.renderDiv || '#renderDiv'
  const renderElement = document.querySelector(renderDiv)
  ReactDOM.unmountComponentAtNode(renderElement)
}

export { initLayerManager, destroy }
